import { useMediaQuery, useTheme } from '@material-ui/core';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { pushNav } from '../../../../../core/src/action';
import {
  GrainBinSiteFragmentFragment,
  GrainBinSiteLink,
  OpsViewRowVisibilityPrefsFragmentFragment,
  SaveOperationsViewRowVisibilityPrefsMutationVariables,
  UpdateGrainBinMutationVariables,
  withCreateGrainBinSiteHoc,
  WithCreateGrainBinSiteHocChildProps,
  withDeleteGrainBinSiteHoc,
  WithDeleteGrainBinSiteHocChildProps,
  withGetAccountHoc,
  WithGetAccountHocChildProps,
  withGetGrainBinSitesHoc,
  WithGetGrainBinSitesHocChildProps,
  withGetOpsViewColsVisibilityPrefsHoc,
  WithGetOpsViewColsVisibilityPrefsHocChildProps,
  withGetOpsViewRowsVisibilityPrefsHoc,
  WithGetOpsViewRowsVisibilityPrefsHocChildProps,
  withRenameGrainBinSiteHoc,
  WithRenameGrainBinSiteHocChildProps,
  withSaveOperationsViewRowVisibilityPrefsHoc,
  WithSaveOperationsViewRowVisibilityPrefsHocChildProps,
  withSaveOperationsViewUserLevelPrefsHoc,
  WithSaveOperationsViewUserLevelPrefsHocChildProps,
  withUpdateGrainBinHoc,
  WithUpdateGrainBinHocChildProps,
  withUpdateGrainBinSiteLinkHoc,
  WithUpdateGrainBinSiteLinkHocChildProps,
} from '../../../../../core/src/api';
import { AggregatedSitesContext } from '../../../../../core/src/contexts';
import { BackArrowIcon } from '../../../../../core/src/media';
import { black_shade_8 } from '../../../../../core/src/style';
import { DialogSpinner } from '../../spinner';
import { GrainBinAggregationDataType } from '../GrainOperations';
import { getValidOperationsViewRowsVisibilityPrefs } from '../OperationsViewTableHelpers';
import { ManageSitesTable, PrefJsonItem } from './ManageSitesTable';

export const UNASSIGNED_SITE_NAME = 'UNASSIGNED_BINS';
export const UNASSIGNED_BINS_SITE_ID = -1;
export const UNASSIGNED_SITE_DISPLAY_NAME = 'UNGROUPED BINS';
const ManageSitesViewBase: FunctionComponent<
  {
    grain_bin_ids: number[];
    goContainer: (args: any) => void;
    user_id: number;
    account_id: number;
  } & WithGetGrainBinSitesHocChildProps &
    WithCreateGrainBinSiteHocChildProps &
    WithRenameGrainBinSiteHocChildProps &
    WithDeleteGrainBinSiteHocChildProps &
    WithUpdateGrainBinSiteLinkHocChildProps &
    WithUpdateGrainBinHocChildProps &
    WithGetAccountHocChildProps &
    WithSaveOperationsViewUserLevelPrefsHocChildProps &
    WithSaveOperationsViewRowVisibilityPrefsHocChildProps &
    WithGetOpsViewColsVisibilityPrefsHocChildProps &
    WithGetOpsViewRowsVisibilityPrefsHocChildProps &
    RouteComponentProps
> = ({
  user_id,
  account_id,
  grain_bin_ids,
  grain_bin_sites,
  grain_sites_loading,
  grainSitesRefetch,
  goContainer,
  createGrainBinSite,
  deleteGrainBinSite,
  renameGrainBinSite,
  updateGrainBinSiteLink,
  updateGrainBin,
  refecthAccount,
  saveOperationsViewRowVisibilityPrefs,
  saveOperationsViewUserLevelPrefs,
  loading,
  rows_visibility_prefs_loading,
  operations_view_rows_visibility_prefs,
  operations_view_cols_visibility_prefs,
}) => {
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const dispatch = useDispatch();

  // In aggregated (special viewer) view, use the sites data passed via context
  // instead of the HOC query result which would be empty for special viewer accounts.
  const aggregated_ctx = React.useContext(AggregatedSitesContext);
  const effective_grain_bin_sites: any[] = aggregated_ctx ? aggregated_ctx.sites : grain_bin_sites;
  const effective_grain_sites_loading = aggregated_ctx
    ? aggregated_ctx.loading
    : grain_sites_loading;

  // get tree data
  console.log('grain_bin_sites', effective_grain_bin_sites);
  const is_aggregated_view = effective_grain_bin_sites.some((site) => site.account_name != null);

  const grainBins: GrainBinAggregationDataType[] = effective_grain_bin_sites.reduce(
    (acc: any, site) => {
      if (site && site.bins.length > 0) acc.push(...site.bins);
      return acc;
    },
    []
  );

  // Build a map from grain_bin_id → account_name for aggregated view
  const bin_account_name_map = new Map<number, string>();
  if (is_aggregated_view) {
    effective_grain_bin_sites.forEach((site) => {
      site.bins.forEach((bin) => {
        if (bin) bin_account_name_map.set(bin.grain_bin_id, site.account_name);
      });
    });
  }

  console.log('grainBins', grainBins);

  const grainBinsHierarchyData = grainBins.map((bin) => {
    const grain_bin_attributes: any = bin.grain_bin_attributes;
    const site_name = grain_bin_attributes.site
      ? grain_bin_attributes.site.site_name
      : 'UNASSIGNED_BINS';
    const bin_name = bin.grain_bin_attributes.bin_name;
    const site_id = grain_bin_attributes.site
      ? grain_bin_attributes.site.site_id
      : UNASSIGNED_BINS_SITE_ID;

    let siteHierarchy: string[];
    if (is_aggregated_view) {
      const acct_name = bin_account_name_map.get(bin.grain_bin_id) || 'Unknown Account';
      // Ungrouped bins go directly under account; named-site bins get Account > Site > Bin
      siteHierarchy =
        site_name === UNASSIGNED_SITE_NAME
          ? [acct_name, bin_name]
          : [acct_name, site_name, bin_name];
    } else {
      siteHierarchy = [site_name, bin_name];
    }

    return {
      bin_name,
      siteHierarchy,
      site_id,
      id: `bin-${bin.grain_bin_id}`,
      bin_id: bin.grain_bin_id,
    };
  });
  console.log('grainBinsHierarchyData', grainBinsHierarchyData);
  const unassignedBins = grainBinsHierarchyData.filter(
    (bin) => bin.site_id === UNASSIGNED_BINS_SITE_ID
  );
  const unassignedBinsIds = unassignedBins.map(({ bin_id }) => bin_id);

  console.log('unassignedBinsIds in manage sites', unassignedBinsIds);

  let grainSitesHierarchyData: any[] = [];

  if (is_aggregated_view) {
    // Build account-level group rows, then site rows under each account
    const seen_accounts = new Set<string>();
    effective_grain_bin_sites.forEach((site) => {
      if (site.account_name && !seen_accounts.has(site.account_name)) {
        seen_accounts.add(site.account_name);
        grainSitesHierarchyData.push({
          id: `account_${site.account_name}`,
          site_id: `account_${site.account_name}`,
          siteHierarchy: [site.account_name],
        });
      }
    });
    // Named site rows (Account > Site)
    effective_grain_bin_sites
      .filter(
        (site) =>
          site.site_id !== UNASSIGNED_BINS_SITE_ID && site.site_name !== UNASSIGNED_SITE_NAME
      )
      .forEach((site) => {
        grainSitesHierarchyData.push({
          id: `site-${site.site_id}`,
          site_id: site.site_id,
          siteHierarchy: [site.account_name, site.site_name],
        });
      });
  } else {
    grainSitesHierarchyData = effective_grain_bin_sites
      .filter((site) => site.site_id !== UNASSIGNED_BINS_SITE_ID)
      .map((site) => ({
        id: `site-${site.site_id}`,
        site_id: site.site_id,
        siteHierarchy: [site.site_name],
      }));
    grainSitesHierarchyData.push({
      siteHierarchy: [UNASSIGNED_SITE_NAME],
      site_id: UNASSIGNED_BINS_SITE_ID,
      id: `site-${UNASSIGNED_BINS_SITE_ID}`,
    });
  }

  console.log('grainSitesHierarchyData', grainSitesHierarchyData);

  // merge sites and bins hierarchy data
  const sitesHierarchyData = [...grainSitesHierarchyData, ...grainBinsHierarchyData];

  console.log('sitesHierarchyData', sitesHierarchyData);

  const allActiveSitesIds: number[] = effective_grain_bin_sites.map((site) => site.site_id);
  const liveSitesBins: Record<number, number[]> = {};
  // Build bin_id → live site_id map from grainBinsHierarchyData (already has live site_id per bin)
  const liveBinSites: Record<number, number> = {};
  effective_grain_bin_sites.forEach((site) => {
    if (site.site_id !== UNASSIGNED_BINS_SITE_ID) {
      liveSitesBins[site.site_id] = site.bins.map((b) => b.grain_bin_id);
    }
  });
  grainBinsHierarchyData.forEach((bin) => {
    liveBinSites[bin.bin_id] = bin.site_id;
  });
  const valid_operations_view_rows_visibility_prefs = getValidOperationsViewRowsVisibilityPrefs({
    allActiveSitesIds,
    operations_view_rows_visibility_prefs,
    unassignedBinsIds,
    liveSitesBins,
    liveBinSites,
  });
  const updated_operations_view_rows_visibility_prefs: OpsViewRowVisibilityPrefsFragmentFragment | null = valid_operations_view_rows_visibility_prefs
    ? {
        account_id,
        pref_json: JSON.stringify({ sites: valid_operations_view_rows_visibility_prefs }),
        __typename: 'RowVisibilityPref',
      }
    : null;

  const goToOperationsView = () => {
    dispatch(pushNav({ path: '/grain/operations' }));
  };

  const renameGrainSite = async (site_id: number, site_name: string) => {
    try {
      // setIsUpdating(true);
      const result = await renameGrainBinSite({ site_id, site_name });
      console.log('renamed site', { result });
    } catch (error) {
      console.error(error);
    } finally {
      // setIsUpdating(false);
    }
  };

  const createGrainSite = async (
    site_name: string
  ): Promise<GrainBinSiteFragmentFragment | null> => {
    try {
      const result = await createGrainBinSite({ account_id, site_name });
      console.log('created site', { result });
      return result;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const deleteGrainSite = async (site_id: number) => {
    try {
      const result = await deleteGrainBinSite({ site_id });
      console.log('deleted site', { result });
    } catch (error) {
      console.error(error);
    }
  };

  const moveGrainBinIntoSite = async (
    grain_bin_id: number,
    site_id: number
  ): Promise<GrainBinSiteLink | null> => {
    console.log('moveGrainBinIntoSite', grain_bin_id, site_id);
    try {
      const result = await updateGrainBinSiteLink({ grain_bin_id, site_id });
      console.log('moved bin', { result });
      return result;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const renameGrainBin = async (grain_bin_id: number, bin_name: string) => {
    // Guard: in aggregated view, only allow renaming bins that belong to the special viewer's
    // own account. Bins from child accounts must be renamed by the child account's users.
    if (is_aggregated_view) {
      const bin_site = effective_grain_bin_sites.find((site) =>
        site.bins.some((b) => b.grain_bin_id === grain_bin_id)
      );
      if (bin_site && bin_site.account_name != null) {
        console.warn('Rename blocked: bin belongs to a child account', { grain_bin_id, bin_site });
        return;
      }
    }
    try {
      const variables: UpdateGrainBinMutationVariables = {
        grain_bin_id,
        bin_name,
        location: null,
      };
      const result = await updateGrainBin(variables);
      console.log('updated bin', { result });
      await refecthAccount();
    } catch (error) {
      console.error(error);
    }
  };

  const updateOperationsViewRowVisibilityPrefs = async (newSiteRows: PrefJsonItem[]) => {
    try {
      const variables: SaveOperationsViewRowVisibilityPrefsMutationVariables = {
        account_id,
        pref_json: JSON.stringify({ sites: newSiteRows }),
      };
      const result = await saveOperationsViewRowVisibilityPrefs(variables);
      console.log('updated prefs', { result });
    } catch (error) {
      console.error(error);
    }
  };

  if (effective_grain_sites_loading || loading || rows_visibility_prefs_loading) {
    return <DialogSpinner open title="Loading Sites..." />;
  }
  console.log('sitesHierarchyData', sitesHierarchyData);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        // height: isMobileScreen ? 'calc(100vh - 136px)' : 'calc(100vh - 210px)',
        flexDirection: 'column',
        marginLeft: 20,
        marginRight: 20,
        width: isWideScreen ? '1030px' : '100%',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          margin: 20,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            marginRight: 10,
            cursor: 'pointer',
          }}
        >
          <BackArrowIcon onClick={goToOperationsView} />
        </div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: black_shade_8 }}>
          Manage Table
        </h1>
      </div>
      <ManageSitesTable
        user_id={user_id}
        account_id={account_id}
        data={sitesHierarchyData}
        grainSitesRefetch={grainSitesRefetch}
        deleteGrainSite={deleteGrainSite}
        renameGrainSite={renameGrainSite}
        createGrainSite={createGrainSite}
        moveGrainBinIntoSite={moveGrainBinIntoSite}
        renameGrainBin={renameGrainBin}
        operations_view_cols_visibility_prefs={operations_view_cols_visibility_prefs}
        operations_view_rows_visibility_prefs={updated_operations_view_rows_visibility_prefs}
        saveOperationsViewUserLevelPrefs={saveOperationsViewUserLevelPrefs}
        saveOperationsViewRowVisibilityPrefs={saveOperationsViewRowVisibilityPrefs}
        updateOperationsViewRowVisibilityPrefs={updateOperationsViewRowVisibilityPrefs}
      />
    </div>
  );
};

export const ManageSitesView = withGetAccountHoc(
  withGetOpsViewRowsVisibilityPrefsHoc(
    withGetOpsViewColsVisibilityPrefsHoc(
      withSaveOperationsViewRowVisibilityPrefsHoc(
        withSaveOperationsViewUserLevelPrefsHoc(
          withUpdateGrainBinHoc(
            withUpdateGrainBinSiteLinkHoc(
              withCreateGrainBinSiteHoc(
                withDeleteGrainBinSiteHoc(
                  withRenameGrainBinSiteHoc(withGetGrainBinSitesHoc(ManageSitesViewBase))
                )
              )
            )
          )
        )
      )
    )
  )
);
